import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Button } from "@mui/material";
import { URL } from "./../../constants/userConstants";
import { API } from "../../actions/userAction";
import { useAlert } from "react-alert";
import { useSelector } from "react-redux";
import Navbar from "../navbar";
import Sidebar from "../Sidebar";
import { getDisplayDate } from "../../utils/dateformat";
import Loader from "../loader";

/* ================= STYLES ================= */

const Container = styled.div`
  margin: 2rem auto;
  padding: 1rem;

  @media screen and (min-width: 600px) {
    margin-left: 220px;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
  padding: 2rem;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
`;

const FilterContainer = styled.div`
  margin-bottom: 1rem;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const TypeButton = styled(Button)`
  && {
    border-radius: 20px;
    text-transform: none;
    font-weight: 600;
    padding: 6px 18px;
  }
`;

const TableWrapper = styled.div`
  width: 100%;

  @media (max-width: 600px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem;
  background: #f5f5f5;
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #eee;
`;

const Tr = styled.tr`
  transition: 0.2s;

  &:hover {
    background: #f9f9f9;
    transform: scale(1.01);
  }
`;

const Status = styled.span`
  font-weight: 600;
  color: ${(props) =>
    props.status === "success"
      ? "green"
      : props.status === "pending"
      ? "orange"
      : "red"};
`;

const TypeBadge = styled.span`
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;

  background-color: ${(props) =>
    props.type === "deposit"
      ? "#e6f4ea"
      : props.type === "withdraw"
      ? "#fdecea"
      : props.type === "winnings"
      ? "#e3f2fd"
      : "#fff3e0"};

  color: ${(props) =>
    props.type === "deposit"
      ? "green"
      : props.type === "withdraw"
      ? "red"
      : props.type === "winnings"
      ? "blue"
      : "orange"};
`;

const MobileCard = styled.div`
  display: none;

  @media (max-width: 600px) {
    display: block;
    background: #fff;
    padding: 12px;
    border-radius: 10px;
    margin-bottom: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

/* ================= COMPONENT ================= */

const MyTransactions = () => {
  const { user } = useSelector((state) => state.user);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const alert = useAlert();

  const fetchTransactions = async () => {
    try {
      if (user?._id) {
        setLoading(true);
        const res = await API.get(
          `${URL}/payment/my-transactions/${user._id}`
        );
        setTransactions(res.data || []);
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      alert.error("Failed to fetch transactions");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [user]);

  const filteredTransactions =
    filterType === "all"
      ? transactions
      : transactions.filter(
          (tx) => tx.action.toLowerCase() === filterType
        );

  return (
    <>
      <Navbar />
      <Sidebar />

      <Container>
        <Card>
          <Title>My Transactions</Title>

          {/* FILTER BUTTONS */}
          <FilterContainer>
            <TypeButton
              variant={filterType === "all" ? "contained" : "outlined"}
              onClick={() => setFilterType("all")}
            >
              All
            </TypeButton>

            <TypeButton
              variant={filterType === "deposit" ? "contained" : "outlined"}
              onClick={() => setFilterType("deposit")}
              color="success"
            >
              Deposit
            </TypeButton>

            <TypeButton
              variant={filterType === "withdraw" ? "contained" : "outlined"}
              onClick={() => setFilterType("withdraw")}
              color="error"
            >
              Withdraw
            </TypeButton>
          </FilterContainer>

          {/* LOADING */}
          {loading ? (
            <Loader />
          ) : filteredTransactions.length === 0 ? (
            <p>No transactions found.</p>
          ) : (
            <>
              {/* DESKTOP TABLE */}
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Amount</Th>
                      <Th>Date</Th>
                      <Th>Status</Th>
                      <Th>Type</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((tx) => (
                      <Tr key={tx._id}>
                        <Td>₹ {tx.amount}</Td>
                        <Td>{getDisplayDate(tx.createdAt)}</Td>
                        <Td>
                          <Status status={tx.status}>
                            {tx.status}
                          </Status>
                        </Td>
                        <Td>
                          <TypeBadge type={tx.action}>
                            {tx.action}
                          </TypeBadge>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>

              {/* MOBILE VIEW */}
              {filteredTransactions.map((tx) => (
                <MobileCard key={tx._id}>
                  <p>
                    <strong>₹ {tx.amount}</strong>
                  </p>
                  <p>{getDisplayDate(tx.createdAt)}</p>
                  <Status status={tx.status}>
                    {tx.status}
                  </Status>
                  <br />
                  <TypeBadge type={tx.action}>
                    {tx.action}
                  </TypeBadge>
                </MobileCard>
              ))}
            </>
          )}
        </Card>
      </Container>
    </>
  );
};

export default MyTransactions;
